import React, { useMemo, useState } from "react";
import {
    Box, Table, TableHeader, TableRow, TableCell, TableBody,
    Text, Button, Layer, Heading, Notification, Tabs, Tab,
    TextArea, CheckBox, Tip
} from "grommet";
import { StatusGood, User, FormEdit } from "grommet-icons";
import axios from "axios";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const FREE_EMAIL_ORGS = new Set([
    "gmail",
    "googlemail",
    "yahoo",
    "ymail",
    "hotmail",
    "outlook",
    "live",
    "msn",
    "protonmail",
    "pm",
    "icloud",
    "me",
    "aol",
    "gmx",
    "zoho",
    "mail",
    "mailfence",
    "yandex",
    "fastmail",
    "hey",
    "duck",
]);

const toTitle = (str = "") =>
    str
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");

const normalizeOrg = (raw = "") => {
    const cleaned = String(raw).replace(/[^a-zA-Z0-9]+/g, " ").trim();
    if (!cleaned) return "N/A";
    const lower = cleaned.toLowerCase();
    if (FREE_EMAIL_ORGS.has(lower)) return "Individual Account";
    return toTitle(cleaned);
};

// Derive an organisation name from the email domain (drops common TLD/ccTLD suffixes)
export const getOrganisationFromEmail = (email = "") => {
    if (!email) return "N/A";
    const [, domainPart = ""] = String(email).split("@");
    if (!domainPart) return "N/A";

    const parts = domainPart.split(".").filter(Boolean);
    if (parts.length === 0) return "N/A";

    let base = "";
    if (
        parts.length >= 3 &&
        parts[parts.length - 1].length === 2 &&
        ["co", "com", "net", "org"].includes(parts[parts.length - 2])
    ) {
        base = parts[parts.length - 3];
    } else if (parts.length >= 2) {
        base = parts[parts.length - 2];
    } else {
        base = parts[0];
    }

    return normalizeOrg(base);
};

const ManagerAssessmentsTable = ({ assessmentData = [], orgFilter = [] }) => {
    const [selected, setSelected] = useState(null);
    const [showUMLayer, setShowUMLayer] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [activeTab, setActiveTab] = useState(0);
    const [updatingMDnc, setUpdatingMDnc] = useState(false);
    const [savingMContact, setSavingMContact] = useState(false);
    const [mCommentOpen, setMCommentOpen] = useState(false);
    const [mCommentDraft, setMCommentDraft] = useState("");

    // Build one display row per assessment group (pull manager comms fields from first entry)
    const rows = useMemo(() => {
        return (assessmentData || []).map((g) => {
            const entries = Array.isArray(g.entries) ? g.entries : [];
            const first = entries[0] || {};
            const completedCount = entries.filter((e) => e.is_complete === 1).length;

            return {
                assessment_id: g.assessment_id,
                assessment_name: g.assessment_meta?.assessment_name || "N/A",
                creation_date: g.assessment_meta?.creation_date || null,
                creation_time: g.assessment_meta?.creation_time || null,
                manager_id: g.assessment_meta?.manager_id ?? null,

                manager_name: first.manager_name || "N/A",
                manager_email: first.manager_email || null,
                manager_country: first.manager_country || "N/A",

                // manager-level comms pulled from first entry
                manager_agree_to_communicate:
                    first.manager_agree_to_communicate != null
                        ? Number(first.manager_agree_to_communicate)
                        : null,
                manager_is_communicated:
                    first.manager_is_communicated != null
                        ? Number(first.manager_is_communicated)
                        : 0,
                manager_communicated_at_date: first.manager_communicated_at_date || null,
                manager_communicated_at_time: first.manager_communicated_at_time || null,
                manager_comment: first.manager_comment ?? null,

                users_count: entries.length,
                completed_count: completedCount,
                entries,
                raw: g,
            };
        });
    }, [assessmentData]);

    // sort newest created first
    const sorted = useMemo(() => {
        return [...rows].sort((a, b) => {
            const da = a.creation_date ? new Date(a.creation_date).getTime() : 0;
            const db = b.creation_date ? new Date(b.creation_date).getTime() : 0;
            return db - da;
        });
    }, [rows]);

    const displayed = useMemo(
        () =>
            sorted.filter((row) => {
                if (!orgFilter.length) return true;
                const org = getOrganisationFromEmail(row.manager_email);
                return orgFilter.includes(org);
            }),
        [orgFilter, sorted]
    );

    const openUM = (row) => {
        setSelected(row);
        setActiveTab(0);
        setShowUMLayer(true);
    };
    const closeUM = () => {
        setShowUMLayer(false);
        setSelected(null);
        setMCommentOpen(false);
        setMCommentDraft("");
    };

    const openRecs = (row) => {
        window.open(`/manager/${row.assessment_id}/recommendations`, "_blank");
    };

    // ----- Manager actions (keep selected + entries[0] in sync) -----
    const handleToggleManagerDnc = async (isDnc) => {
        if (!selected?.assessment_id || !selected?.manager_id) return;
        setUpdatingMDnc(true);
        try {
            const agree = isDnc ? 0 : 1; // 0=DNC, 1=can contact
            await axios.put("/api/pursuit/manager-agree-to-communicate", {
                manager_id: selected.manager_id,
                agree_to_communicate: agree,
            });

            setSelected((prev) => {
                if (!prev) return prev;
                const entries = (prev.entries || []).map((e, idx) =>
                    idx === 0 ? { ...e, manager_agree_to_communicate: agree } : e
                );
                return { ...prev, manager_agree_to_communicate: agree, entries };
            });

            setToastMessage(
                `Manager ${isDnc ? "marked Do Not Contact" : "set to contactable"}.`
            );
            window.location.reload();
        } catch (e) {
            setToastMessage("Failed to update manager contact preference.");
        } finally {
            setUpdatingMDnc(false);
        }
    };

    const markManagerAsContacted = async () => {
        if (!selected?.assessment_id || !selected?.manager_id) return;
        setSavingMContact(true);
        try {
            await axios.post("/api/pursuit/mark-manager-as-contacted", {
                manager_id: selected.manager_id,
            });

            const now = new Date();
            const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
            const timeStr = now.toLocaleTimeString();

            setSelected((prev) => {
                if (!prev) return prev;
                const entries = (prev.entries || []).map((e, idx) =>
                    idx === 0
                        ? {
                            ...e,
                            manager_is_communicated: 1,
                            manager_communicated_at_date: dateStr,
                            manager_communicated_at_time: timeStr,
                        }
                        : e
                );
                return {
                    ...prev,
                    manager_is_communicated: 1,
                    manager_communicated_at_date: dateStr,
                    manager_communicated_at_time: timeStr,
                    entries,
                };
            });

            setToastMessage("Marked manager as contacted.");
            window.location.reload(setTimeout(() => { }, 2000));
        } catch (e) {
            setToastMessage("Failed to mark manager as contacted.");
        } finally {
            setSavingMContact(false);
        }
    };

    const saveManagerComment = async () => {
        if (!selected?.assessment_id || !selected?.manager_id) return;
        try {
            await axios.put(`/api/pursuit/${selected.manager_id}/manager/comment`, {
                comment: mCommentDraft ?? "",
            });
            setSelected((prev) => {
                if (!prev) return prev;
                const entries = (prev.entries || []).map((e, idx) =>
                    idx === 0 ? { ...e, manager_comment: mCommentDraft ?? "" } : e
                );
                return { ...prev, manager_comment: mCommentDraft ?? "", entries };
            });
            setMCommentOpen(false);
            setToastMessage("Comment saved.");
            window.location.reload(setTimeout(() => { }, 2000));
        } catch (e) {
            setToastMessage("Failed to save manager comment.");
        }
    };

    const hasMComment = Boolean(selected?.manager_comment);
    const mCommentText = selected?.manager_comment ?? "";
    const isManagerContacted = Number(selected?.manager_is_communicated) === 1;
    const truncateWords = (str = "", n = 12) => {
        const s = String(str || "").trim();
        if (!s) return "";
        const words = s.split(/\s+/);
        return words.length <= n ? s : words.slice(0, n).join(" ") + "…";
    };


    return (
        <>
            <Box overflow="auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell><Text weight="bold">#</Text></TableCell>
                            <TableCell><Text weight="bold">Assessment Name</Text></TableCell>
                            <TableCell><Text weight="bold">Created At</Text></TableCell>
                            <TableCell><Text weight="bold">Users</Text></TableCell>
                            <TableCell><Text weight="bold">Completed</Text></TableCell>
                            <TableCell><Text weight="bold">Manager Name</Text></TableCell>
                            <TableCell><Text weight="bold">Manager Email</Text></TableCell>
                            <TableCell><Text weight="bold">Organisation</Text></TableCell>
                            <TableCell><Text weight="bold">Manager Country</Text></TableCell>
                            <TableCell><Text weight="bold">Manager Contacted</Text></TableCell>
                            <TableCell><Text weight="bold">Comment</Text></TableCell>
                            <TableCell align="center"><Text weight="bold">Actions</Text></TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {displayed.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={12} align="center">No assessments found.</TableCell>
                            </TableRow>
                        ) : (
                            displayed.map((row, i) => (
                                <TableRow key={row.assessment_id}>
                                    <TableCell><Text>{i + 1}</Text></TableCell>
                                    <TableCell>
                                        {(() => {
                                            const c = (row.assessment_name) || "";
                                            const normalized = c.trim();
                                            const isNA = !normalized || normalized === "N/A";

                                            return isNA ? (
                                                <Text>N/A</Text>
                                            ) : (
                                                <Tip
                                                    content={
                                                        <Box pad="xsmall" width={{ max: "medium" }}>
                                                            <Text size="small">{normalized}</Text>
                                                        </Box>
                                                    }
                                                    dropProps={{ align: { top: "bottom" } }}
                                                >
                                                    <Text>{truncateWords(normalized, 4)}</Text>
                                                </Tip>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>
                                        <Text>
                                            {fmtDate(row.creation_date)}
                                            {row.creation_time ? ` ${row.creation_time}` : ""}
                                        </Text>
                                    </TableCell>
                                    <TableCell><Text>{row.users_count}</Text></TableCell>
                                    <TableCell><Text>{row.completed_count}</Text></TableCell>
                                    <TableCell><Text>{row.manager_name}</Text></TableCell>
                                    <TableCell><Text>{row.manager_email}</Text></TableCell>
                                    <TableCell><Text>{getOrganisationFromEmail(row.manager_email)}</Text></TableCell>
                                    <TableCell><Text>{row.manager_country}</Text></TableCell>
                                    <TableCell>
                                        {row.manager_communicated_at_date && row.manager_communicated_at_time ? (
                                            <Text color="brand">
                                                {new Intl.DateTimeFormat("en-US", {
                                                    dateStyle: "medium",
                                                }).format(new Date(`${row.manager_communicated_at_date}T${row.manager_communicated_at_time}`))}{" "}
                                                at{" "}
                                                {new Intl.DateTimeFormat("en-US", {
                                                    hour: "numeric",
                                                    minute: "2-digit",
                                                    hour12: true,
                                                }).format(new Date(`1970-01-01T${row.manager_communicated_at_time}`))}
                                            </Text>
                                        ) : (
                                            <Text color="status-critical">Manager not contacted</Text>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const c = (row.manager_comment) || "";
                                            const normalized = c.trim();
                                            const isNA = !normalized || normalized === "N/A";

                                            return isNA ? (
                                                <Text>N/A</Text>
                                            ) : (
                                                <Tip
                                                    content={
                                                        <Box pad="xsmall" width={{ max: "medium" }}>
                                                            <Text size="small">{normalized}</Text>
                                                        </Box>
                                                    }
                                                    dropProps={{ align: { top: "bottom" } }}
                                                >
                                                    <Text>{truncateWords(normalized, 4)}</Text>
                                                </Tip>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box direction="row" justify="center" gap="xsmall">
                                            <Button icon={<User />} tip="Manager & Users" onClick={() => openUM(row)} color="brand" size="medium" />
                                            <Button icon={<StatusGood />} tip="Recommendations" onClick={() => openRecs(row)} color="brand" size="medium" />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </Box>

            {/* Manager + Users Details Layer */}
            {showUMLayer && selected && (
                <Layer onEsc={closeUM} onClickOutside={closeUM} responsive={false} position="center" animation={false}>
                    <Box pad="medium" width="xlarge" height={{ max: "80vh" }} gap="small">
                        <Heading level={3} margin="none">
                            Assessment: {selected.assessment_name}
                        </Heading>

                        <Tabs activeIndex={activeTab} onActive={(i) => setActiveTab(i)}>
                            {/* Tab 1: Manager Details */}
                            <Tab title="Manager Details">
                                <Box gap="xsmall" pad={{ vertical: "small" }}>
                                    <Text><strong>Name:</strong> {selected.manager_name || "N/A"}</Text>
                                    <Text><strong>Email:</strong> {selected.manager_email || "N/A"}</Text>
                                    <Text><strong>Country:</strong> {selected.manager_country || "N/A"}</Text>

                                    {/* Agreed + DNC inline */}
                                    <Box direction="row" align="center" gap="medium" wrap margin={{ top: "small" }}>

                                        <CheckBox
                                            label={<Text>Manager asked not to contact</Text>}
                                            checked={selected.manager_agree_to_communicate === 0}
                                            onChange={(e) => handleToggleManagerDnc(e.target.checked)}
                                            disabled={updatingMDnc}
                                        />
                                    </Box>

                                    {/* Contact buttons */}
                                    <Box direction="row" gap="small" align="center" margin={{ top: "small" }}>
                                        <Button
                                            label="Email Manager"
                                            onClick={() => window.open(`mailto:${selected.manager_email}`)}
                                            primary
                                            size="small"
                                            disabled={!selected.manager_email}
                                        />
                                        <Button
                                            label="Mark Manager as Contacted"
                                            onClick={markManagerAsContacted}
                                            primary
                                            size="small"
                                            disabled={isManagerContacted || savingMContact}
                                        />
                                    </Box>

                                    {/* Last contacted stamp (if any) */}
                                    {isManagerContacted && (
                                        <Text margin={{ top: "xsmall" }}>
                                            <strong>Contacted:</strong>{" "}
                                            {selected.manager_communicated_at_date || "—"}{" "}
                                            {selected.manager_communicated_at_time || ""}
                                        </Text>
                                    )}

                                    {/* Comment section */}
                                    <Box margin={{ top: "medium" }} gap="xsmall">
                                        {!hasMComment ? (
                                            <Box direction="row" align="center" justify="between">
                                                <Text weight="bold">Comment</Text>
                                                <Button
                                                    label="Add comment"
                                                    onClick={() => { setMCommentDraft(""); setMCommentOpen(true); }}
                                                    secondary
                                                    size="small"
                                                />
                                            </Box>
                                        ) : (
                                            <>
                                                <Box direction="row" align="center" justify="between">
                                                    <Box direction="row" align="center" gap="xsmall">
                                                        <Text weight="bold">Comment</Text>
                                                        <Button
                                                            icon={<FormEdit />}
                                                            onClick={() => { setMCommentDraft(mCommentText); setMCommentOpen(true); }}
                                                            plain
                                                            tip="Edit comment"
                                                        />
                                                    </Box>
                                                </Box>
                                                <Text>{mCommentText}</Text>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                            </Tab>

                            {/* Tab 2: Users */}
                            <Tab title={`Users (${selected.completed_count} completed)`}>
                                <Box pad={{ vertical: "small" }}>
                                    <Box height="50vh" overflow={{ vertical: "auto", horizontal: "auto" }}>
                                        <Table style={{ minWidth: 900 }}>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableCell scope="col"><Text weight="bold">#</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">User Name</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Email</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Organisation</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Country</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Completed</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Start</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">End</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Total</Text></TableCell>
                                                    <TableCell scope="col"><Text weight="bold">Topics</Text></TableCell>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {(selected.entries || []).map((e, idx) => {
                                                    const topics = [
                                                        e.topic1_name, e.topic2_name, e.topic3_name, e.topic4_name, e.topic5_name,
                                                    ].filter(Boolean).join("; ");
                                                    const startStr = [fmtDate(e.start_date), e.start_time || ""].filter(Boolean).join(" ");
                                                    const endStr = [fmtDate(e.end_date), e.end_time || ""].filter(Boolean).join(" ");
                                                    return (
                                                        <TableRow key={e.id ?? `${e.user_id}-${idx}`}>
                                                            <TableCell><Text>{idx + 1}</Text></TableCell>
                                                            <TableCell><Text>{e.user_name || e.user_email || "—"}</Text></TableCell>
                                                            <TableCell><Text>{e.user_email || "—"}</Text></TableCell>
                                                            <TableCell><Text>{getOrganisationFromEmail(e.user_email)}</Text></TableCell>
                                                            <TableCell><Text>{e.user_country || "—"}</Text></TableCell>
                                                            <TableCell>
                                                                <Text color={e.is_complete === 1 ? "status-ok" : "status-critical"}>
                                                                    {e.is_complete === 1 ? "Yes" : "No"}
                                                                </Text>
                                                            </TableCell>
                                                            <TableCell><Text>{startStr || "—"}</Text></TableCell>
                                                            <TableCell><Text>{endStr || "—"}</Text></TableCell>
                                                            <TableCell><Text>{(e.query_count ?? "—") - (e.correct_count ?? 0)}/{e.query_count ?? "—"}</Text></TableCell>
                                                            <TableCell><Text>{topics || "—"}</Text></TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Box>
                            </Tab>
                        </Tabs>

                        <Box align="end" margin={{ top: "small" }}>
                            <Button label="Close" onClick={closeUM} secondary />
                        </Box>
                    </Box>

                    {toastMessage && (
                        <Notification
                            toast
                            status="normal"
                            title="Update"
                            message={toastMessage}
                            onClose={() => setToastMessage("")}
                        />
                    )}

                    {/* Manager Comment Modal */}
                    {mCommentOpen && (
                        <Layer onEsc={() => setMCommentOpen(false)} onClickOutside={() => setMCommentOpen(false)}>
                            <Box pad="medium" width="medium" gap="small">
                                <Heading level={4} margin="none">
                                    {hasMComment ? "Edit comment" : "Add comment"}
                                </Heading>
                                <TextArea
                                    value={mCommentDraft}
                                    onChange={(e) => setMCommentDraft(e.target.value)}
                                    resize={false}
                                    placeholder="Type your comment..."
                                    rows={6}
                                />
                                <Box direction="row" gap="small" justify="end" margin={{ top: "small" }}>
                                    <Button label="Cancel" onClick={() => setMCommentOpen(false)} />
                                    <Button primary label="Save" onClick={saveManagerComment} />
                                </Box>
                            </Box>
                        </Layer>
                    )}
                </Layer>
            )}
        </>
    );
};

export default ManagerAssessmentsTable;
